import { cn } from "../ui/cn";

/**
 * A company's name, opening its page in a new tab.
 *
 * Every filing queue asks somebody to decide about records they do not know by
 * name, and the only honest answer to "are these two the same company" is to
 * go and look at one of them. Until this existed the names in the grouping
 * panel were label text -- clicking one ticked a radio, which is not what
 * clicking a company's name means anywhere else in the CRM.
 *
 * A new tab rather than a route change, for the same reason `ExistingCompanies`
 * opens one: the person is part-way through an answer that is not saved
 * anywhere. The grouping panel holds which record is the group and which of
 * fourteen depots were unticked, all of it component state, and navigating the
 * SPA unmounts the page and throws the lot away. Somebody who went to check
 * one depot would come back to a blank answer and have to re-do it, which is
 * how a queue gets abandoned half-filed.
 *
 * A plain `<a>`, not react-router's `<Link>`: it is leaving this tab, so the
 * client-side router has nothing to offer, and `<Link target="_blank">` only
 * ends up doing a full load anyway.
 *
 * Nested inside a `<label>` on purpose, and safe there -- a label does nothing
 * for clicks targeted at interactive content inside it, so the name navigates
 * and the rest of the row still ticks the control beside it.
 */
export function CompanyLink({
  id,
  name,
  className,
}: {
  id: number;
  name: string;
  className?: string;
}) {
  return (
    <a
      href={`/crm/companies/${id}`}
      target="_blank"
      rel="noreferrer"
      title={`Open ${name} in a new tab`}
      className={cn(
        /* Underlined on hover rather than always. In a queue every row is a
           company and every company is a link, so a permanent underline
           striped the whole list and made a set of names look like a set of
           actions. */
        "hover:text-brand-600 hover:underline hover:underline-offset-2 dark:hover:text-brand-400",
        className,
      )}
    >
      {name}
    </a>
  );
}
